<template>
  <div :id="id" class="tree" style="min-width: auto !important">
    <div v-for="file in files" :key="file.id" class="section">
      <div
        class="file d-flex align-items-center my-1 border rounded px-3 py-2"
        @dragover="(e) => dragOver(e, file.id)"
      >
        <!-- DRAG BUTTON -->
        <span draggable @dragstart="(e) => dragStart(e, file.id)" @dragend="dragEnd" style="color: #999">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            height="16"
            width="16"
            focusable="false"
            role="img"
            viewBox="0 0 16 16"
          >
            <rect width="16" height="1" x="0" y="1" />
            <rect width="16" height="1" x="0" y="6" />
            <rect width="16" height="1" x="0" y="11" />
          </svg>
        </span>
        <span class="pl-2" :class="{ 'col-3': audio }">{{ file.name }}</span>

        <div class="ml-auto d-flex align-items-center" :class="{ 'col-8 px-0': audio }">
          <audio-player v-if="audio" :src="getDropboxAudioSrc(file.url)" />

          <a v-if="file.url" :href="file.url" target="_blank" title="Открыть файл в новой вкладке">
            <b-icon-box-arrow-up-right />
          </a>

          <b-icon-x v-if="file.url" class="file-remove rounded ml-3" scale="1.5" @click="deleteFile(file.id)" />
          <b-spinner v-else class="align-middle" variant="primary" small />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.file-remove {
  stroke: #999;
  cursor: pointer;
  &:hover {
    stroke: #666 !important;
  }
  &:active {
    stroke: #333 !important;
  }
}
.tree {
  display: flex;
  flex-direction: column;
  .section {
    position: relative;
    .file span[draggable] {
      cursor: grab;
    }
  }
}

#drag-placeholder {
  .line {
    margin-left: -1px;
    margin-right: -1px;
    height: 2px;
    background-color: #5867dd;
  }
  .arrow {
    width: 0;
    height: 0;
    border: 5px solid transparent;
    &.left {
      border-right-color: #5867dd;
      border-left: none;
    }
    &.right {
      border-left-color: #5867dd;
      border-right: none;
    }
  }
}
</style>

<script>
import { BIconX, BIconBoxArrowUpRight } from "bootstrap-vue";
import AudioPlayer from "shared/widgets/AudioPlayer.vue";

export default {
  name: "FileTree",
  props: {
    value: {
      type: Array,
      required: false,
      default: [],
    },
    audio: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BIconX,
    BIconBoxArrowUpRight,
    AudioPlayer,
  },
  computed: {
    placement: () => ({
      beforeFile: -1,
      afterFile: 1,
    }),
  },
  data() {
    return {
      id: null,
      draggedId: null,
      draggedOver: {
        fileId: null,
        placement: null,
      },
      files: this.value,
    };
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.files = this.value;
      },
    },
  },
  created() {
    this.id = "FileTree_" + Date.now();
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {},
    /* DRAG */
    dragStart(e, fileId) {
      if (e.target.classList.contains("name")) {
        e.target.style = "pointer-events: none !important;";
      }

      e.target.style.opacity = 0.5;
      this.draggedId = fileId;
      this.draggedOver = {
        fileId: fileId,
        placement: this.placement.beforeFile,
      };
    },
    dragEnd({ target }) {
      target.style.opacity = 1;
      this.removeDragPlaceholder();

      if (this.draggedId === this.draggedOver.fileId) {
        return this.resetDragData();
      }

      let draggedFile = this.files.find((b) => b.id === this.draggedId);
      const draggedOverFile = this.files.find((b) => b.id === this.draggedOver.fileId);
      const newWeight = draggedOverFile.weight + this.draggedOver.placement * 5;
      draggedFile.weight = newWeight;

      const untouchedFiles = this.files.filter((f) => f.weight < newWeight);
      const filesToMove = this.files.filter((f) => f.weight > newWeight);

      draggedFile.weight = Number(Math.ceil(draggedFile.weight / 10) * 10);

      const movedFiles = filesToMove.map((f, i) => {
        f.weight = draggedFile.weight + (i + 1) * 10;
        return f;
      });

      this.files = [...untouchedFiles, draggedFile, ...movedFiles];

      this.broadcastChanges();
      this.resetDragData();
    },
    resetDragData() {
      this.draggedId = null;
      this.draggedOver = {
        fileId: null,
        placement: null,
      };
    },
    dragOver(e, fileId) {
      if (!this.draggedId) {
        return;
      }

      // calculate where mouse is relative to the dragged over element
      const fileContainer = e.target;
      const clientRect = fileContainer.getBoundingClientRect();
      const percentage = (e.clientY - clientRect.top) / clientRect.height;
      const placement = Math.sign(percentage - 0.5);
      this.draggedOver = {
        fileId: fileId,
        placement: placement,
      };

      this.removeDragPlaceholder();
      const dragPlaceholder = this.createDragPlaceholder();
      dragPlaceholder.setAttribute("style", `${placement === this.placement.afterFile ? "bottom" : "top"}: -5px`);
      fileContainer.insertAdjacentElement(
        placement === this.placement.afterFile ? "afterend" : "beforebegin",
        dragPlaceholder
      );
    },
    createDragPlaceholder() {
      const newPlaceholder = Object.assign(document.createElement("div"), {
        id: "drag-placeholder",
        classList: "w-100 d-flex align-items-center justify-content-center position-absolute",
      });
      newPlaceholder.append(
        Object.assign(document.createElement("div"), {
          classList: "arrow right",
        }),
        Object.assign(document.createElement("div"), {
          classList: "line w-100 border-0",
        }),
        Object.assign(document.createElement("div"), {
          classList: "arrow left",
        })
      );
      return newPlaceholder;
    },
    removeDragPlaceholder() {
      const previousPlaceholder = document.getElementById("drag-placeholder");
      previousPlaceholder && previousPlaceholder.remove();
    },
    deleteFile(fileId) {
      this.files = this.files.filter((f) => f.id !== fileId);
      this.broadcastChanges();
    },
    broadcastChanges() {
      this.$emit("input", this.files);
      this.$emit("change");
    },
    getDropboxAudioSrc(dbUrl) {
      return dbUrl.split("?")[0].replace("://www.", "://dl.") + '?rlkey=' + (new URL(dbUrl)).searchParams.get('rlkey');
    },
  },
};
</script>
